// src/components/padrekko/qc/QcCurrentPadLayer.jsx
//
// Renders the current pad's AOI outline + name on the map. Navigational
// anchor for the QC inspector — operator always knows which pad they're
// "on" without reading the panel.
//
// Panel-mounted (same lifecycle as QcInspector + QcFindingsLayer +
// QcPolygonDrawer): tab switch unmounts → cleanup removes layers from
// the map. Hides during drawingPolygon so the canvas snapshot stays
// clean of both the outline and the label.
//
// Color + line-width match PadLayer.jsx's accent-teal pad outline so
// the operator's "teal = pad" mental model from the Volumetrics tab
// carries over. No fill — outline only — because this is a singular
// navigational marker, not a feature in a multi-pad context.
//
// Label uses the dominant codebase convention (Open Sans Regular,
// slate-900 halo); slate-50 text is slightly more prominent than the
// grid-label slate-300 because this is the focused-on anchor, not
// background context.
//
// Label is rendered off a POINT, not the pad polygon. A symbol layer with
// point placement on a polygon gets placed once PER overlapping vector tile
// (GeoJSON sources are internally tiled), so large pads duplicated the name at
// high zoom — and text-allow-overlap disabled the collision dedup that would
// have hidden the copies. point-on-feature gives a single anchor inside the
// polygon, so the name renders exactly once at every zoom.
//
// Four effects, same skeleton as QcFindingsLayer:
//
//   1. Mount/unmount: add source + line + label, remove on cleanup.
//   2. Source data sync: update on pad change.
//   3. Visibility: hide while drawingPolygon.
//   4. (No zoom subscription — QcInspector's auto-zoom-to-pad handles
//      camera, this layer just renders.)

import { useEffect, useMemo } from "react";
import pointOnFeature from "@turf/point-on-feature";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore } from "../../../stores/useQcStore";

const SOURCE_ID       = "qc-current-pad";
const LABEL_SOURCE_ID = "qc-current-pad-label-src";
const LINE_LAYER_ID   = "qc-current-pad-line";
const LABEL_LAYER_ID  = "qc-current-pad-label";

// Matches PadLayer.jsx:94-99 exactly — same zoom interpolation so the
// outline weight reads identically across the two tabs.
const TEAL_LINE_WIDTH = [
  "interpolate", ["linear"], ["zoom"],
  10, 0.8,
  14, 1.4,
  17, 2.0,
];

const LABEL_TEXT_SIZE = [
  "interpolate", ["linear"], ["zoom"],
  10, 11,
  14, 14,
  17, 18,
];

function buildFeatureCollection(pad) {
  if (!pad?.aoi_geom?.type) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: pad.aoi_geom,
        properties: { name: pad.pad_name ?? "" },
      },
    ],
  };
}

// A single Point feature (anchor inside the polygon) that carries the pad
// name — see the header note on why the label can't ride the polygon.
function buildLabelFeatureCollection(pad) {
  if (!pad?.aoi_geom?.type) {
    return { type: "FeatureCollection", features: [] };
  }
  let anchor;
  try {
    anchor = pointOnFeature({ type: "Feature", geometry: pad.aoi_geom, properties: {} });
  } catch {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: anchor.geometry,
        properties: { name: pad.pad_name ?? "" },
      },
    ],
  };
}

export default function QcCurrentPadLayer() {
  const mapInstance    = useAppViewStore((s) => s.mapInstance);
  const session        = useQcStore((s) => s.session);
  const currentPadIdx  = useQcStore((s) => s.currentPadIdx);
  const drawingPolygon = useQcStore((s) => s.drawingPolygon);

  const pad = session?.pads?.[currentPadIdx ?? 0];

  const featureCollection = useMemo(
    () => buildFeatureCollection(pad),
    // Keyed on aoi_geom + pad_name. Neither changes during a session
    // (set once on load, immutable until next loadSession), so this
    // memo effectively recomputes only on pad navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pad?.aoi_geom, pad?.pad_name]
  );

  const labelFeatureCollection = useMemo(
    () => buildLabelFeatureCollection(pad),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pad?.aoi_geom, pad?.pad_name]
  );

  // ── 1. mount/unmount: add source + line + label ──────────────────────
  useEffect(() => {
    if (!mapInstance) return;

    if (!mapInstance.getSource(SOURCE_ID)) {
      mapInstance.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!mapInstance.getSource(LABEL_SOURCE_ID)) {
      mapInstance.addSource(LABEL_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!mapInstance.getLayer(LINE_LAYER_ID)) {
      mapInstance.addLayer({
        id: LINE_LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        paint: {
          "line-color":   "#00d4ff",
          "line-width":   TEAL_LINE_WIDTH,
          "line-opacity": 1.0,
        },
      });
    }
    if (!mapInstance.getLayer(LABEL_LAYER_ID)) {
      mapInstance.addLayer({
        id: LABEL_LAYER_ID,
        type: "symbol",
        source: LABEL_SOURCE_ID,
        layout: {
          "text-field": ["get", "name"],
          "text-font":  ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size":  LABEL_TEXT_SIZE,
          "text-anchor": "center",
          // Single label per pad, no neighbors to fight — allow overlap
          // so it never disappears at low zooms.
          "text-allow-overlap":   true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color":      "#f8fafc",   // slate-50
          "text-halo-color": "#0f172a",   // slate-900 — codebase convention
          "text-halo-width": 1.5,
        },
      });
    }

    return () => {
      // Layers first, then source — MapLibre rejects removeSource while
      // any layer still references it.
      if (mapInstance.getLayer(LABEL_LAYER_ID)) mapInstance.removeLayer(LABEL_LAYER_ID);
      if (mapInstance.getLayer(LINE_LAYER_ID))  mapInstance.removeLayer(LINE_LAYER_ID);
      if (mapInstance.getSource(LABEL_SOURCE_ID)) mapInstance.removeSource(LABEL_SOURCE_ID);
      if (mapInstance.getSource(SOURCE_ID))     mapInstance.removeSource(SOURCE_ID);
    };
  }, [mapInstance]);

  // ── 2. source data sync ──────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance) return;
    const src = mapInstance.getSource(SOURCE_ID);
    if (src) src.setData(featureCollection);
    const labelSrc = mapInstance.getSource(LABEL_SOURCE_ID);
    if (labelSrc) labelSrc.setData(labelFeatureCollection);
  }, [mapInstance, featureCollection, labelFeatureCollection]);

  // ── 3. visibility toggle: hide during polygon draw ───────────────────
  // Same clean-canvas guarantee as QcFindingsLayer — neither outline
  // nor label may appear in the image AI analyzes.
  useEffect(() => {
    if (!mapInstance) return;
    const visibility = drawingPolygon ? "none" : "visible";
    if (mapInstance.getLayer(LINE_LAYER_ID)) {
      mapInstance.setLayoutProperty(LINE_LAYER_ID, "visibility", visibility);
    }
    if (mapInstance.getLayer(LABEL_LAYER_ID)) {
      mapInstance.setLayoutProperty(LABEL_LAYER_ID, "visibility", visibility);
    }
  }, [mapInstance, drawingPolygon]);

  return null;
}
